class TileCalculator extends HTMLElement {
  constructor() {
    super();

    this.packPrice = parseFloat(this.getAttribute('data-pack-price'));
    this.packSizeM2 = parseFloat(this.getAttribute('data-pack-m2'));
    this.packSizeFt2 = parseFloat(this.getAttribute('data-pack-ft2'));
    this.taxRate = parseFloat(this.dataset.taxRate) / 100;
    this.currency = this.getAttribute("data-currency");
    this.unitToggle = this.querySelector('.js-toggle-units');
    this.measurementToggle = this.querySelector('.js-toggle-measurements');
    this.form = this.querySelector('.js-tile-calculator-form');
    this.fields = this.form.querySelectorAll('.js-field');
    this.inputs = this.form.querySelectorAll('.js-input');
    this.labels = this.form.querySelectorAll('.js-label');
    this.checkbox = this.form.querySelector('.js-checkbox');
    this.errorMessages = this.form.querySelectorAll('.js-error');
    this.submitButton = this.form.querySelector('.js-submit');
    this.updateButton = this.form.querySelector('.js-update');
    this.results = this.querySelector('.js-results');
    this.activeMeasurementType = "area";
    this.activeUnitType = "M";
    this.sqUnit = "SQ";
    this.addWastage = false;
    this.resultsCalculated = false;
    this.totalPacks = 0;

    this.addListeners();
  }

  addListeners() {
    this.unitToggle.addEventListener("change", event => this.toggleUnits(event));
    this.measurementToggle.addEventListener("change", event => this.toggleMeasurements(event));
    this.checkbox.addEventListener("change", () => this.checkboxChange());
    this.inputs.forEach(input => input.addEventListener("input", () => this.handleInputChange()));
    this.form.addEventListener("submit", event => this.calculateTiles(event));
    this.updateButton.addEventListener('click', event => this.updateProductQuantity(event));
  }

  toggleUnits({ target: { value } }) {
    this.activeUnitType = value;

    this.updateForm("units");
  }

  toggleMeasurements({ target: { value } }) {
    this.activeMeasurementType = value;

    this.updateForm();
  }

  updateForm(toggleType) {
    if (toggleType === "units") this.updateUnits();
    else this.updateFields();

    if (this.hasValues()) this.resetInputs();
    if (this.resultsCalculated) this.hideResultsDisplay();
    if (this.hasValues() && this.resultsCalculated) this.updateCalculateButton(true);
    else this.updateCalculateButton();

    this.hideErrorMessages();
  }

  updateUnits() {
    this.labels.forEach(label => this.updateUnit(label));
    this.errorMessages.forEach(label => this.updateUnit(label));
  }

  updateUnit(label) {
    const isArea = label.classList.contains("js-area");
    const content = `${isArea ? this.sqUnit : ''}${this.activeUnitType}`;

    label.textContent = label.textContent.replace(/\(.*?\)/, `(${content})`);
  }

  updateFields() {
    this.fields.forEach(field => {
      field.classList.toggle("hidden");

      this.updateInputs(field);
    });
  }

  updateCalculateButton(recalculate = false) {
    const calculateText = this.submitButton.querySelector(".js-calculate");
    const recalculateText = this.submitButton.querySelector(".js-recalculate");

    this.submitButton.classList.toggle("button--outline", recalculate);

    calculateText.classList.toggle("hidden", recalculate);
    recalculateText.classList.toggle("hidden", !recalculate);

    calculateText.setAttribute("aria-hidden", recalculate);
    recalculateText.setAttribute("aria-hidden", !recalculate);
  }

  updateInputs(field) {
    const input = field.querySelector('.js-input');

    input.setAttribute("aria-required", field.classList.contains(`js-field-${this.activeMeasurementType}`));
  }

  resetInputs() {
    this.inputs.forEach(input => input.value = "");
  }

  showResultsDisplay() {
    this.results.classList.remove("hidden");
  }

  hideResultsDisplay() {
    this.results.classList.add("hidden");
  }

  checkboxChange() {
    this.addWastage = !this.addWastage;

    if (this.hasValues && this.resultsCalculated) this.updateCalculateButton(true);
    else this.updateCalculateButton();
  }

  handleInputChange() {
    if (this.hasValues() && this.resultsCalculated) this.updateCalculateButton(true);
  }

  hasValues() {
    let hasValues = false;

    this.inputs.forEach(input => input.value !== "" && (hasValues = true));

    return hasValues;
  }

  calculateTiles(event) {
    event.preventDefault();

    const totalArea = this.calculateTotalArea();
    const totalPacks = this.calculateTotalPacks(totalArea);

    this.totalPacks = Math.ceil(totalPacks);

    this.showResults();
  }

  calculateTotalArea() {
    return this.activeMeasurementType === "area" ? this.calculateArea() : this.calculateLength();
  }

  calculateArea() {
    const areaInput = this.form.querySelector(".js-field-area .js-input");

    return parseFloat(areaInput.value);
  }

  calculateLength() {
    const lengthInputs = this.form.querySelectorAll(".js-field-dimensions .js-input");

    return Array.from(lengthInputs).reduce((total, input) => total * parseFloat(input.value), 1);
  }

  calculateTotalPacks(totalArea) {
    const packSize = this.activeUnitType === "M" ? this.packSizeM2 : this.packSizeFt2;
    const totalPacks = totalArea / packSize;

    return (this.addWastage ? 1.1 : 1) * totalPacks;
  }

  showResults() {
    if (!this.hasValidInputs()) return;
    else this.hideErrorMessages();

    this.resultsCalculated = true;

    const resultPrice = this.querySelector(".js-result-price");
    const resultTax = this.querySelector(".js-result-tax");
    const resultPacks = this.querySelector(".js-result-packs");
    const totalPrice = this.totalPacks * this.packPrice;
    const currencyFormat = window.storeName === "UK" ? `${this.currency}{{amount}}` : `{{amount}} AED`;

    resultPrice.textContent = Shopify.formatMoney(totalPrice, currencyFormat);
    resultPacks.textContent = `${Math.round(this.totalPacks)}`;

    if (this.taxRate > 0) {
      const resultTaxText = Shopify.formatMoney((totalPrice) * (1.0 - this.taxRate), currencyFormat);

      resultTax.textContent = window.taxString.excludingTaxPrice.replace('{{ price }}', resultTaxText);
    }

    this.showResultsDisplay();
  }

  hasValidInputs() {
    const fields = this.form.querySelectorAll(`.js-field-${this.activeMeasurementType}`);

    let isValid = true;

    fields.forEach(field => {
      const input = field.querySelector('.js-input');

      if (input.value === "") {
        isValid = false;

        this.showError(field);
      }
    });

    return isValid;
  }

  showError(field) {
    const errorMessage = field.querySelector('.js-error');

    errorMessage.classList.remove("hidden");
  }

  hideErrorMessages() {
    this.errorMessages.forEach(error => error.classList.add("hidden"));
  }

  updateProductQuantity(event) {
    event.preventDefault();

    const quantity = document.querySelector('.js-quantity-input');
    const tileCalculatorDrawer = this.closest('tile-calculator-drawer');

    if (quantity) {
      quantity.value = this.totalPacks;

      tileCalculatorDrawer.close();
    }
  }

  resetState() {
    this.form.reset();
    this.resultsCalculated = false;
    this.addWastage = false;
    this.activeUnitType = "M";
    this.totalPacks = 0;

    this.hideResultsDisplay();
    this.hideErrorMessages();
    this.updateCalculateButton();
    this.updateUnits();

    if (this.activeMeasurementType === "dimensions") this.toggleMeasurements({ target: "area" })
  }
}

customElements.define("tile-calculator", TileCalculator);